/* eslint-disable no-console */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { sessionManager } from './security-utils';
import { useAuth } from './use-auth';

interface SessionContextType {
  sessionId: string;
  isSessionValid: boolean;
  updateActivity: () => void;
  timeUntilExpiry: number; // minutes
}

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const SessionContext = createContext<SessionContextType | null>(null);

export interface SessionProviderProps {
  children: React.ReactNode;
  enableActivityTracking?: boolean;
  enableDebugLogs?: boolean;
}

/**
 * Session provider that tracks user activity and session validity
 */
export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
  enableActivityTracking = true,
  enableDebugLogs = false,
}) => {
  const [sessionId] = useState(() => crypto.randomUUID());
  const [isSessionValid, setIsSessionValid] = useState(true);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(60);
  const auth = useAuth();

  const updateActivity = useCallback(() => {
    sessionManager.updateActivity(sessionId);
    if (enableDebugLogs) {
      console.log('Session activity updated:', sessionId);
    }
  }, [sessionId, enableDebugLogs]);

  // Check session validity periodically
  useEffect(() => {
    const checkSession = () => {
      const valid = sessionManager.isSessionValid(sessionId);
      setIsSessionValid(valid);

      if (!valid && auth.isAuthenticated) {
        if (enableDebugLogs) {
          console.warn('Session expired, logging out user');
        }

        auth.logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkSession, 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [sessionId, auth, enableDebugLogs]);

  // Calculate time until expiry
  useEffect(() => {
    const updateTimeUntilExpiry = () => {
      // This is a simplified calculation - you might want to make it more accurate
      const sessionTimeoutMs = 60 * 60 * 1000; // 60 minutes default
      const now = Date.now();
      const sessionStart = now; // You'd track actual session start time
      const expiryTime = sessionStart + sessionTimeoutMs;
      const remaining = Math.max(0, Math.floor((expiryTime - now) / (60 * 1000)));
      setTimeUntilExpiry(remaining);
    };

    updateTimeUntilExpiry();
    const interval = setInterval(updateTimeUntilExpiry, 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Global activity tracking
  useEffect(() => {
    if (!enableActivityTracking) {
      return;
    }

    const activities = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    // eslint-disable-next-line @typescript-eslint/no-restricted-types
    let throttleTimer: NodeJS.Timeout | null = null;

    const throttledUpdateActivity = () => {
      if (throttleTimer) {
        return;
      }

      throttleTimer = setTimeout(() => {
        updateActivity();
        throttleTimer = null;
      }, 30000); // Update every 30 seconds max
    };

    activities.forEach(activity => {
      document.addEventListener(activity, throttledUpdateActivity, true);
    });

    return () => {
      activities.forEach(activity => {
        document.removeEventListener(activity, throttledUpdateActivity, true);
      });
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enableActivityTracking, updateActivity]);

  const contextValue: SessionContextType = {
    sessionId,
    isSessionValid,
    updateActivity,
    timeUntilExpiry,
  };

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};

/**
 * Hook to access session management functionality
 */
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
};

/**
 * Hook to get just the session ID (lighter weight)
 */
export const useSessionId = (): string => {
  const { sessionId } = useSession();
  return sessionId;
};

/**
 * Component to display session status (useful for debugging)
 */
export const SessionStatus: React.FC<{ className?: string }> = ({
  className = 'text-xs text-gray-500',
}) => {
  const { isSessionValid, timeUntilExpiry } = useSession();

  return (
    <div className={className}>
      Session: {isSessionValid ? '✅ Active' : '❌ Expired'}
      {isSessionValid && ` (${timeUntilExpiry}m left)`}
    </div>
  );
};
